import {
  createContext, ReactNode, useCallback, useContext, useState,
  FC,
} from "react";
import Toast from "./Toast";
import styles from "./toast.module.scss";

export type ToastType = "success" | "error" | "warning";

export interface ToastOptions {
  message: string;
  type: ToastType;
  iconClass: string;
  duration?: number;
}

interface ToastOptionsWithId extends ToastOptions {
  id: string;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastOptionsWithId[]>([]);

  const showToast = useCallback((options: ToastOptions) => {
    const id = Date.now().toString();
    setToasts((prevToasts) => [...prevToasts, { id, ...options }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            iconClass={toast.iconClass}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
